import React, { useState, useCallback } from 'react';
import { Button, Slider, InputNumber, Select, Typography, Tabs, Tooltip, Switch } from 'antd';
import {
    CloseOutlined,
    ColumnWidthOutlined,
} from '@ant-design/icons';
import { Textbox, Path } from 'fabric';

const { Text } = Typography;

// ─── Typographic Grid Presets ───
const GRID_PRESETS = [
    { key: 'poster', label: 'Poster', cols: 1, rows: 3, colGutter: 0, rowGutter: 0, margin: 60 },
    { key: 'magazine-2', label: 'Magazine 2-col', cols: 2, rows: 4, colGutter: 24, rowGutter: 24, margin: 48 },
    { key: 'magazine-3', label: 'Magazine 3-col', cols: 3, rows: 6, colGutter: 20, rowGutter: 20, margin: 40 },
    { key: 'editorial-4', label: 'Editorial 4-col', cols: 4, rows: 8, colGutter: 16, rowGutter: 16, margin: 32 },
    { key: 'swiss', label: 'Swiss Grid', cols: 6, rows: 6, colGutter: 12, rowGutter: 12, margin: 24 },
];

// ─── Layout Composition Presets ───
// All positions and sizes are proportional to the media dimensions (cw, ch).
// Margins default to 5% of the smaller dimension when grid is not active.
const LAYOUT_PRESETS = [
    {
        key: 'poster-hero',
        icon: '▌',
        name: 'Poster Hero',
        desc: 'Bold headline + subline + fine print',
        create: (cw, ch, grid) => {
            const m = grid ? Math.min(grid.marginLeft, cw * 0.15) : Math.round(Math.min(cw, ch) * 0.05);
            const w = cw - m * 2;
            return [
                { text: 'HEADLINE', fontSize: Math.round(cw * 0.1), fontWeight: 900, fontFamily: 'Bebas Neue', top: m, left: m, width: w, textAlign: 'left', fill: '#ffffff', charSpacing: 80, lineHeight: 0.95 },
                { text: 'Subheadline text goes here', fontSize: Math.max(12, Math.round(cw * 0.03)), fontWeight: 400, fontFamily: 'Inter', top: m + Math.round(cw * 0.12), left: m, width: w, textAlign: 'left', fill: '#c9d1d9', charSpacing: 20, lineHeight: 1.3 },
                { text: 'Fine print · Additional details', fontSize: Math.max(10, Math.round(cw * 0.018)), fontWeight: 400, fontFamily: 'Space Mono', top: ch - m - Math.round(ch * 0.05), left: m, width: w, textAlign: 'left', fill: '#6e7681', charSpacing: 40, lineHeight: 1.4 },
            ];
        },
    },
    {
        key: 'editorial-stack',
        icon: '≡',
        name: 'Editorial Stack',
        desc: 'Stacked all-caps, tight leading',
        create: (cw, ch, grid) => {
            const m = grid ? Math.min(grid.marginLeft, cw * 0.15) : Math.round(Math.min(cw, ch) * 0.05);
            const w = cw - m * 2;
            const baseSize = Math.round(cw * 0.07);
            const startY = ch * 0.3;
            return [
                { text: 'FIRST LINE', fontSize: baseSize, fontWeight: 900, fontFamily: 'Oswald', top: startY, left: m, width: w, textAlign: 'left', fill: '#ffffff', charSpacing: 60, lineHeight: 1.0, _textTransform: 'uppercase' },
                { text: 'SECOND LINE', fontSize: baseSize, fontWeight: 400, fontFamily: 'Oswald', top: startY + baseSize * 1.05, left: m, width: w, textAlign: 'left', fill: '#a78bfa', charSpacing: 60, lineHeight: 1.0, _textTransform: 'uppercase' },
                { text: 'THIRD LINE', fontSize: baseSize, fontWeight: 900, fontFamily: 'Oswald', top: startY + baseSize * 2.1, left: m, width: w, textAlign: 'left', fill: '#ffffff', charSpacing: 60, lineHeight: 1.0, _textTransform: 'uppercase' },
            ];
        },
    },
    {
        key: 'magazine-callout',
        icon: '❝',
        name: 'Magazine Callout',
        desc: 'Pull quote with decorative glyph',
        create: (cw, ch, grid) => {
            const m = grid ? Math.min(grid.marginLeft, cw * 0.15) : Math.round(Math.min(cw, ch) * 0.05);
            const pad = Math.round(cw * 0.03); // proportional inner padding
            const w = cw - m * 2;
            return [
                { text: '"', fontSize: Math.round(cw * 0.18), fontWeight: 400, fontFamily: 'Playfair Display', top: ch * 0.2, left: m, width: w, textAlign: 'left', fill: '#a78bfa', opacity: 0.3, lineHeight: 0.8 },
                { text: 'A striking pull quote that commands attention', fontSize: Math.max(12, Math.round(cw * 0.04)), fontWeight: 400, fontFamily: 'Playfair Display', fontStyle: 'italic', top: ch * 0.32, left: m + pad, width: w - pad * 2, textAlign: 'left', fill: '#c9d1d9', charSpacing: 10, lineHeight: 1.4 },
                { text: '— Author Name', fontSize: Math.max(10, Math.round(cw * 0.02)), fontWeight: 600, fontFamily: 'Inter', top: ch * 0.32 + Math.round(ch * 0.18), left: m + pad, width: w - pad * 2, textAlign: 'left', fill: '#8b949e', charSpacing: 80, lineHeight: 1.4, _textTransform: 'uppercase' },
            ];
        },
    },
    {
        key: 'title-body',
        icon: '¶',
        name: 'Title + Body',
        desc: 'Centered title with body text',
        create: (cw, ch, grid) => {
            const m = grid ? Math.min(grid.marginLeft, cw * 0.15) : Math.round(Math.min(cw, ch) * 0.05);
            const w = cw - m * 2;
            const titleSize = Math.round(cw * 0.06);
            return [
                { text: 'Title Here', fontSize: titleSize, fontWeight: 700, fontFamily: 'Space Grotesk', top: m, left: m, width: w, textAlign: 'center', fill: '#ffffff', charSpacing: 20, lineHeight: 1.1 },
                { text: 'Body text goes here. Add your paragraph content. Text will wrap naturally within the text box.', fontSize: Math.max(11, Math.round(cw * 0.022)), fontWeight: 400, fontFamily: 'DM Sans', top: m + titleSize * 1.5, left: m, width: w, textAlign: 'center', fill: '#8b949e', charSpacing: 0, lineHeight: 1.6 },
            ];
        },
    },
    {
        key: 'split-type',
        icon: '⫿',
        name: 'Split Type',
        desc: 'Two-column headline split',
        create: (cw, ch, grid) => {
            const m = grid ? Math.min(grid.marginLeft, cw * 0.15) : Math.round(Math.min(cw, ch) * 0.05);
            const gap = Math.round(cw * 0.03);
            const halfW = (cw - m * 2 - gap) / 2;
            const sz = Math.round(cw * 0.06);
            return [
                { text: 'LEFT\nSIDE', fontSize: sz, fontWeight: 900, fontFamily: 'Archivo Black', top: ch * 0.35, left: m, width: halfW, textAlign: 'left', fill: '#ffffff', charSpacing: 40, lineHeight: 1.0 },
                { text: 'RIGHT\nSIDE', fontSize: sz, fontWeight: 900, fontFamily: 'Archivo Black', top: ch * 0.35, left: m + halfW + gap, width: halfW, textAlign: 'right', fill: '#a78bfa', charSpacing: 40, lineHeight: 1.0 },
            ];
        },
    },
    {
        key: 'vertical-stack',
        icon: '⏐',
        name: 'Vertical Stack',
        desc: 'Rotated 90° for spines / sidebars',
        create: (cw, ch, grid) => {
            const m = grid ? Math.min(grid.marginLeft, cw * 0.15) : Math.round(Math.min(cw, ch) * 0.05);
            return [
                { text: 'VERTICAL TEXT', fontSize: Math.round(ch * 0.04), fontWeight: 700, fontFamily: 'Space Grotesk', top: ch * 0.5, left: m, width: ch - m * 2, textAlign: 'center', fill: '#ffffff', charSpacing: 120, lineHeight: 1.0, _textTransform: 'uppercase', angle: -90 },
            ];
        },
    },
];

// ─── Text Path Shapes ───
const PATH_SHAPES = [
    {
        key: 'arc-up',
        icon: '⌒',
        name: 'Arc Up',
        desc: 'Gentle upward arc',
        createPath: (cw) => {
            const w = cw * 0.6;
            const h = w * 0.25;
            return `M 0 ${h} Q ${w / 2} 0 ${w} ${h}`;
        },
    },
    {
        key: 'arc-down',
        icon: '⌓',
        name: 'Arc Down',
        desc: 'Gentle downward arc',
        createPath: (cw) => {
            const w = cw * 0.6;
            const h = w * 0.25;
            return `M 0 0 Q ${w / 2} ${h} ${w} 0`;
        },
    },
    {
        key: 'circle',
        icon: '○',
        name: 'Circle',
        desc: 'Full circle path',
        createPath: (cw) => {
            const r = cw * 0.2;
            return `M ${r},0 A ${r},${r} 0 1,1 ${r},${r * 2} A ${r},${r} 0 1,1 ${r},0`;
        },
    },
    {
        key: 'wave',
        icon: '∿',
        name: 'Wave',
        desc: 'Sine wave path',
        createPath: (cw) => {
            const w = cw * 0.6;
            const amp = w * 0.08;
            const seg = w / 4;
            return `M 0 ${amp} C ${seg * 0.5} 0 ${seg * 0.5} 0 ${seg} ${amp} S ${seg * 1.5} ${amp * 2} ${seg * 2} ${amp} S ${seg * 2.5} 0 ${seg * 3} ${amp} S ${seg * 3.5} ${amp * 2} ${seg * 4} ${amp}`;
        },
    },
];

// ─── Component ───

const TypographyPresetsPanel = ({ visible, onClose, fabricRef, onLayersAdd, typoGrid, onTypoGridChange }) => {
    const [activeTab, setActiveTab] = useState('grid');

    // ─── Grid Controls ───
    const gridConfig = typoGrid || { enabled: false, cols: 3, rows: 4, colGutter: 20, rowGutter: 20, marginTop: 40, marginRight: 40, marginBottom: 40, marginLeft: 40, color: '#a78bfa', opacity: 0.25, snap: true };

    const updateGrid = useCallback((patch) => {
        onTypoGridChange?.({ ...gridConfig, ...patch });
    }, [gridConfig, onTypoGridChange]);

    const applyGridPreset = useCallback((preset) => {
        onTypoGridChange?.({
            ...gridConfig,
            enabled: true,
            cols: preset.cols,
            rows: preset.rows,
            colGutter: preset.colGutter,
            rowGutter: preset.rowGutter,
            marginTop: preset.margin,
            marginRight: preset.margin,
            marginBottom: preset.margin,
            marginLeft: preset.margin,
        });
    }, [gridConfig, onTypoGridChange]);

    // ─── Helper: compute media bounds fresh from canvas ───
    const getMediaBounds = useCallback(() => {
        const canvas = fabricRef?.current;
        if (!canvas) return null;
        const objects = canvas.getObjects().filter(o => {
            if (o._isCustomGuide || o.excludeFromExport) return false;
            if (o.type === 'textbox' || o.type === 'text' || o.type === 'i-text') return false;
            if (!o.visible) return false;
            return true;
        });
        if (objects.length === 0) return null;
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        objects.forEach(o => {
            const sw = o.getScaledWidth();
            const sh = o.getScaledHeight();
            const c = o.getCenterPoint();
            const l = c.x - sw / 2, r = c.x + sw / 2;
            const t = c.y - sh / 2, b = c.y + sh / 2;
            if (l < minX) minX = l; if (t < minY) minY = t;
            if (r > maxX) maxX = r; if (b > maxY) maxY = b;
        });
        return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    }, [fabricRef]);

    // ─── Place Layout Preset ───
    const placePreset = useCallback((preset) => {
        const canvas = fabricRef?.current;
        if (!canvas) return;

        // Compute media bounds fresh at click time
        const mb = getMediaBounds() || { x: 0, y: 0, w: canvas.width, h: canvas.height };
        const cw = mb.w;
        const ch = mb.h;
        const offsetX = mb.x;
        const offsetY = mb.y;

        const textDefs = preset.create(cw, ch, gridConfig.enabled ? gridConfig : null);
        const newLayers = [];

        textDefs.forEach((def, i) => {
            const { text, angle, _textTransform, ...props } = def;
            const displayText = _textTransform === 'uppercase' ? text.toUpperCase() : text;

            const textbox = new Textbox(displayText, {
                ...props,
                // Offset positions by media origin
                left: (props.left || 0) + offsetX,
                top: (props.top || 0) + offsetY,
                selectable: true,
                evented: true,
                editable: true,
                _textTransform: _textTransform || 'none',
                _rawText: text,
            });

            if (angle != null) {
                textbox.set('angle', angle);
            }

            canvas.add(textbox);

            newLayers.push({
                id: `text_${Date.now()}_${i}`,
                name: def.text.substring(0, 20) || 'Text',
                visible: true,
                locked: false,
                object: textbox,
                sourceUrl: null,
                type: 'text',
            });
        });

        canvas.renderAll();
        onLayersAdd?.(newLayers);
    }, [fabricRef, gridConfig, onLayersAdd, getMediaBounds]);

    // ─── Place Text on Path ───
    const placeTextOnPath = useCallback((pathDef) => {
        const canvas = fabricRef?.current;
        if (!canvas) return;

        // Compute media bounds fresh at click time
        const mb = getMediaBounds() || { x: 0, y: 0, w: canvas.width, h: canvas.height };
        const cw = mb.w;
        const ch = mb.h;
        const offsetX = mb.x;
        const offsetY = mb.y;

        const pathStr = pathDef.createPath(cw);
        const path = new Path(pathStr, {
            fill: null,
            stroke: null,
            visible: false,
        });

        const textbox = new Textbox('Type on path', {
            fontFamily: 'Space Grotesk',
            fontSize: Math.round(cw * 0.035),
            fontWeight: 700,
            fill: '#ffffff',
            charSpacing: 40,
            left: offsetX + cw * 0.2,
            top: offsetY + ch * 0.4,
            width: cw * 0.6,
            path: path,
            selectable: true,
            evented: true,
            editable: true,
        });

        canvas.add(textbox);
        canvas.setActiveObject(textbox);
        canvas.renderAll();

        onLayersAdd?.([{
            id: `text_path_${Date.now()}`,
            name: `${pathDef.name} Text`,
            visible: true,
            locked: false,
            object: textbox,
            sourceUrl: null,
            type: 'text',
        }]);
    }, [fabricRef, onLayersAdd, getMediaBounds]);

    if (!visible) return null;

    // ─── Render helpers ───
    const sliderRow = (label, min, max, value, onChange, step = 1) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 56, whiteSpace: 'nowrap' }}>{label}</Text>
            <Slider min={min} max={max} step={step} value={value} onChange={onChange}
                style={{ flex: 1, margin: 0 }}
                styles={{ track: { background: '#a78bfa' }, rail: { background: '#30363d' }, handle: { borderColor: '#a78bfa', width: 12, height: 12 } }}
            />
            <Text style={{ color: '#a78bfa', fontSize: 11, fontFamily: 'monospace', minWidth: 28, textAlign: 'right' }}>
                {step < 1 ? value.toFixed(2) : value}
            </Text>
        </div>
    );

    const itemCard = (item, onClick) => (
        <div
            key={item.key}
            onClick={onClick}
            style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                transition: 'all 0.15s ease', background: 'transparent',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#21262d'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
            <div style={{
                width: 32, height: 32, borderRadius: 6,
                background: '#21262d', border: '1px solid #30363d',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, flexShrink: 0,
            }}>
                {item.icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#c9d1d9', fontSize: 12, fontWeight: 500 }}>{item.name}</div>
                <div style={{ color: '#6e7681', fontSize: 10, marginTop: 1 }}>{item.desc}</div>
            </div>
        </div>
    );

    // ─── Tab Content ───
    const gridTab = (
        <div style={{ padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Enable toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Text style={{ color: '#c9d1d9', fontSize: 12, fontWeight: 600 }}>Show Grid</Text>
                <Switch
                    size="small"
                    checked={gridConfig.enabled}
                    onChange={(v) => updateGrid({ enabled: v })}
                    style={{ background: gridConfig.enabled ? '#a78bfa' : '#30363d' }}
                />
            </div>

            {/* Grid presets */}
            <div>
                <Text style={{ color: '#a78bfa', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Presets</Text>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                    {GRID_PRESETS.map(p => (
                        <Button
                            key={p.key}
                            size="small"
                            onClick={() => applyGridPreset(p)}
                            style={{
                                color: '#c9d1d9', background: '#21262d', borderColor: '#30363d',
                                fontSize: 11, fontWeight: 500,
                            }}
                        >
                            {p.label}
                        </Button>
                    ))}
                </div>
            </div>

            {/* Columns & Rows */}
            <div>
                <Text style={{ color: '#a78bfa', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Columns & Rows</Text>
                <div style={{ marginTop: 8 }}>
                    {sliderRow('Columns', 1, 12, gridConfig.cols, (v) => updateGrid({ cols: v }))}
                    {sliderRow('Rows', 1, 12, gridConfig.rows, (v) => updateGrid({ rows: v }))}
                </div>
            </div>

            {/* Gutters */}
            <div>
                <Text style={{ color: '#a78bfa', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Gutters</Text>
                <div style={{ marginTop: 8 }}>
                    {sliderRow('Col Gap', 0, 80, gridConfig.colGutter, (v) => updateGrid({ colGutter: v }))}
                    {sliderRow('Row Gap', 0, 80, gridConfig.rowGutter, (v) => updateGrid({ rowGutter: v }))}
                </div>
            </div>

            {/* Margins */}
            <div>
                <Text style={{ color: '#a78bfa', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Margins</Text>
                <div style={{ marginTop: 8 }}>
                    {sliderRow('Top', 0, 200, gridConfig.marginTop, (v) => updateGrid({ marginTop: v }))}
                    {sliderRow('Right', 0, 200, gridConfig.marginRight, (v) => updateGrid({ marginRight: v }))}
                    {sliderRow('Bottom', 0, 200, gridConfig.marginBottom, (v) => updateGrid({ marginBottom: v }))}
                    {sliderRow('Left', 0, 200, gridConfig.marginLeft, (v) => updateGrid({ marginLeft: v }))}
                </div>
            </div>

            {/* Appearance */}
            <div>
                <Text style={{ color: '#a78bfa', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Appearance</Text>
                <div style={{ marginTop: 8 }}>
                    {sliderRow('Opacity', 0.05, 0.6, gridConfig.opacity, (v) => updateGrid({ opacity: v }), 0.01)}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                        <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 56 }}>Color</Text>
                        <div style={{ display: 'flex', gap: 4 }}>
                            {['#a78bfa', '#60a5fa', '#34d399', '#fbbf24', '#f43f5e', '#f472b6', '#ffffff', '#8b949e'].map(c => (
                                <div
                                    key={c}
                                    onClick={() => updateGrid({ color: c })}
                                    style={{
                                        width: 18, height: 18, borderRadius: 4,
                                        background: c,
                                        border: gridConfig.color === c ? '2px solid #fff' : '1px solid #30363d',
                                        cursor: 'pointer',
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Snap toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 4 }}>
                <Text style={{ color: '#c9d1d9', fontSize: 12, fontWeight: 600 }}>Snap to Grid</Text>
                <Switch
                    size="small"
                    checked={gridConfig.snap}
                    onChange={(v) => updateGrid({ snap: v })}
                    style={{ background: gridConfig.snap ? '#a78bfa' : '#30363d' }}
                />
            </div>
        </div>
    );

    const presetsTab = (
        <div style={{ padding: 6 }}>
            {LAYOUT_PRESETS.map(p => itemCard(p, () => placePreset(p)))}
        </div>
    );

    const pathTab = (
        <div style={{ padding: 6 }}>
            {PATH_SHAPES.map(p => itemCard(p, () => placeTextOnPath(p)))}
        </div>
    );

    return (
        <div
            style={{
                position: 'absolute',
                top: 48,
                right: 10,
                width: 300,
                zIndex: 90,
                background: 'rgba(13, 17, 23, 0.97)',
                border: '1px solid #30363d',
                borderRadius: 10,
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                overflow: 'hidden',
                pointerEvents: 'auto',
                maxHeight: 'calc(100vh - 160px)',
                display: 'flex',
                flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {/* Header */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px',
                borderBottom: '1px solid #30363d',
                background: '#161b22',
                borderRadius: '10px 10px 0 0',
                flexShrink: 0,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ColumnWidthOutlined style={{ fontSize: 14 }} />
                    <span style={{ color: '#c9d1d9', fontWeight: 600, fontSize: 13 }}>Typography</span>
                </div>
                <Button type="text" size="small" onClick={onClose}
                    style={{ color: '#8b949e', fontSize: 11, padding: '2px 6px' }}>✕</Button>
            </div>

            {/* Tabs */}
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                size="small"
                centered
                style={{ flex: 1, overflow: 'hidden' }}
                items={[
                    {
                        key: 'grid',
                        label: <span style={{ fontSize: 11, color: activeTab === 'grid' ? '#a78bfa' : '#8b949e' }}>Grid</span>,
                        children: <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 260px)' }}>{gridTab}</div>,
                    },
                    {
                        key: 'presets',
                        label: <span style={{ fontSize: 11, color: activeTab === 'presets' ? '#a78bfa' : '#8b949e' }}>Presets</span>,
                        children: <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 260px)' }}>{presetsTab}</div>,
                    },
                    {
                        key: 'path',
                        label: <span style={{ fontSize: 11, color: activeTab === 'path' ? '#a78bfa' : '#8b949e' }}>Path</span>,
                        children: <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 260px)' }}>{pathTab}</div>,
                    },
                ]}
                tabBarStyle={{
                    margin: 0,
                    borderBottom: '1px solid #30363d',
                    flexShrink: 0,
                }}
            />
        </div>
    );
};

export default TypographyPresetsPanel;
